import { Node, Edge } from "reactflow";
import { FlowData, DecisionVariablesRegistry, GlobalVariableRegistry, ToolCall, InterjectionGroup } from "./types";

// Helper function to parse JSONLogic into frontend conditions format
const parseJsonLogicToConditions = (logic: any): Array<{ variable: string; operator: string; value: any; type: string; joiner: string }> => {
  if (!logic || typeof logic !== "object") {
    return [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }];
  }

  const conditions: Array<{ variable: string; operator: string; value: any; type: string; joiner: string }> = [];

  // First, check if this is a single condition (including complex ones like null checks)
  const singleCondition = parseSingleCondition(logic);
  if (singleCondition) {
    conditions.push({
      ...singleCondition,
      joiner: "and"
    });
    return conditions;
  }

  // Handle "and" logic with multiple conditions
  if (logic.and && Array.isArray(logic.and)) {
    logic.and.forEach((condition: any, index: number) => {
      const parsedCondition = parseSingleCondition(condition);
      if (parsedCondition) {
        conditions.push({
          ...parsedCondition,
          joiner: index === 0 ? "and" : "and"
        });
      }
    });
  }
  // Handle "or" logic with multiple conditions  
  else if (logic.or && Array.isArray(logic.or)) {
    logic.or.forEach((condition: any, index: number) => {
      const parsedCondition = parseSingleCondition(condition);
      if (parsedCondition) {
        conditions.push({
          ...parsedCondition,
          joiner: index === 0 ? "or" : "or"
        });
      }
    });
  }

  // Return at least one condition if parsing resulted in empty array
  return conditions.length > 0 ? conditions : [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }];
};

// Helper function to parse a single JSONLogic condition
const parseSingleCondition = (condition: any): { variable: string; operator: string; value: any; type: string } | null => {
  if (!condition || typeof condition !== "object") {
    return null;
  }

  // Handle equality: {"==": [{"var": "varName"}, value]}
  if (condition["=="]) {
    const [varRef, value] = condition["=="];
    if (varRef?.var) {
      // Convert null comparisons to is_null operator
      if (value === null) {
        return {
          variable: varRef.var,
          operator: "is_null",
          value: "", // Null operators don't need a value
          type: "boolean"
        };
      }
      // Convert boolean comparisons to is_true/is_false operators
      else if (typeof value === "boolean") {
        return {
          variable: varRef.var,
          operator: value === true ? "is_true" : "is_false",
          value: "", // Boolean operators don't need a value
          type: "boolean"
        };
      } else {
        return {
          variable: varRef.var,
          operator: "equals",
          value: value,
          type: "value"
        };
      }
    }
  }

  // Handle inequality: {"!=": [{"var": "varName"}, value]}
  if (condition["!="]) {
    const [varRef, value] = condition["!="];
    if (varRef?.var) {
      // Convert null comparisons to is_not_null operator
      if (value === null) {
        return {
          variable: varRef.var,
          operator: "is_not_null",
          value: "", // Null operators don't need a value
          type: "boolean"
        };
      }
      // Convert boolean comparisons to is_true/is_false operators (inverted)
      else if (typeof value === "boolean") {
        return {
          variable: varRef.var,
          operator: value === true ? "is_false" : "is_true", // Inverted logic for !=
          value: "", // Boolean operators don't need a value
          type: "boolean"
        };
      } else {
        return {
          variable: varRef.var,
          operator: "not_equals",
          value: value,
          type: "value"
        };
      }
    }
  }

  // Handle greater than: {">": [{"var": "varName"}, value]}
  if (condition[">"]) {
    const [varRef, value] = condition[">"];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "greater_than",
        value: value,
        type: "value"
      };
    }
  }

  // Handle less than: {"<": [{"var": "varName"}, value]}
  if (condition["<"]) {
    const [varRef, value] = condition["<"];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "less_than",
        value: value,
        type: "value"
      };
    }
  }

  // Handle greater than or equal: {">=": [{"var": "varName"}, value]}
  if (condition[">="]) {
    const [varRef, value] = condition[">="];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "greater_than_or_equal",
        value: value,
        type: "value"
      };
    }
  }

  // Handle less than or equal: {"<=": [{"var": "varName"}, value]}
  if (condition["<="]) {
    const [varRef, value] = condition["<="];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "less_than_or_equal", 
        value: value,
        type: "value"
      };
    }
  }

  // Handle "in" operator (contains): {"in": [value, {"var": "varName"}]}
  if (condition["in"]) {
    const [value, varRef] = condition["in"];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "contains",
        value: value,
        type: "value"
      };
    }
  }

  // Handle negated "in" operator (not contains): {"!": {"in": [value, {"var": "varName"}]}}
  if (condition["!"] && condition["!"]["in"]) {
    const [value, varRef] = condition["!"]["in"];
    if (varRef?.var) {
      return {
        variable: varRef.var,
        operator: "not_contains", 
        value: value,
        type: "value"
      };
    }
  }

  // Handle null checks: {"or": [{"==": [{"var": "varName"}, null]}, {"==": [{"var": "varName"}, undefined]}, {"==": [{"var": "varName"}, ""]}]}
  if (condition["or"] && Array.isArray(condition["or"])) {
    const orConditions = condition["or"];
    // Check if this is a null check pattern
    if (orConditions.length >= 2) {
      const firstCondition = orConditions[0];
      const secondCondition = orConditions[1];
      
      if (firstCondition["=="] && secondCondition["=="] && 
          firstCondition["=="][1] === null && 
          firstCondition["=="][0]?.var === secondCondition["=="][0]?.var) {
        return {
          variable: firstCondition["=="][0].var,
          operator: "is_null",
          value: "",
          type: "value"
        };
      }
    }
  }

  // Handle not null checks: {"and": [{"!=": [{"var": "varName"}, null]}, {"!=": [{"var": "varName"}, undefined]}, {"!=": [{"var": "varName"}, ""]}]}
  if (condition["and"] && Array.isArray(condition["and"])) {
    const andConditions = condition["and"];
    // Check if this is a not null check pattern
    if (andConditions.length >= 2) {
      const firstCondition = andConditions[0];
      const secondCondition = andConditions[1];
      
      if (firstCondition["!="] && secondCondition["!="] && 
          firstCondition["!="][1] === null && 
          firstCondition["!="][0]?.var === secondCondition["!="][0]?.var) {
        return {
          variable: firstCondition["!="][0].var,
          operator: "is_not_null",
          value: "",
          type: "value"
        };
      }
    }
  }

  return null;
};

// Helper function to extract all variable names from JSONLogic
const extractVariablesFromJsonLogic = (logic: any): string[] => {
  const variables: string[] = [];

  const extractFromObject = (obj: any): void => {
    if (!obj || typeof obj !== "object") return;

    // If this is a variable reference: {"var": "varName"}
    if (obj.var && typeof obj.var === "string") {
      if (!variables.includes(obj.var)) {
        variables.push(obj.var);
      }
      return;
    }

    // Recursively check all properties and array elements
    Object.values(obj).forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(item => extractFromObject(item));
      } else if (typeof value === "object") {
        extractFromObject(value);
      }
    });
  };

  extractFromObject(logic);
  return variables;
};

// Backend interfaces based on the data structure
interface BackendNode {
  role: string;
  content: string;
  category: string;
  is_start_node: boolean;
  is_end_node: boolean;
  decision_logic?: {
    logic: any;
    passed: number;
    failed: number;
  };
  next_possible_node_ids: number[];
  variables?: Record<string, boolean>;
  tool_call?: {
    name: string;
    description?: string;
    arguments: string;
    response_variables?: Record<string, {
      values: string[];
      persist: boolean;
    }>;
  };
}

interface BackendConfig {
  conversation_flow: Record<string, BackendNode>;
  interjections: Record<string, any>;
  placeholder_variables: Record<string, {
    values: string[];
    persist?: boolean;
  }>;
  configs: {
    start_node: number[];
    end_node: number[];
    decision_node_id: number[];
    buildup: boolean;
    deduplicate: boolean;
    canonical: boolean;
    slice_configs: any[];
  };
  tool_calls: Array<{
    name: string;
    arguments: string;
  }>;
  metadata: Record<string, any>;
}

export const convertBackendConfigToFlowData = (backendConfig: BackendConfig): FlowData => {
  const backendNodes = backendConfig.conversation_flow;
  const nodeIds = Object.keys(backendNodes);
  
  // Extract stored node positions from metadata (if available)
  const storedPositions: Record<string, { x: number; y: number }> = backendConfig.metadata?.nodePositions || {};
  
  // Check for MAITAI_PREFIX node and extract system prompt
  let systemPrompt = "";
  const filteredNodeIds = nodeIds.filter(nodeId => {
    const node = backendNodes[nodeId];
    // Detect MAITAI_PREFIX node: assistant role with content starting with "MAITAI_PREFIX"
    if (node.role === "assistant" && node.content && node.content.startsWith("MAITAI_PREFIX")) {
      systemPrompt = node.content.replace("MAITAI_PREFIX", "");
      return false; // Remove MAITAI_PREFIX node from processing
    }
    return true; // Keep all other nodes
  });
  
  // Convert backend nodes to frontend nodes (excluding system node)
  const frontendNodes: Node[] = [];
  const edges: Edge[] = [];
  
  // Track decision variables from all nodes
  const decisionVariables: DecisionVariablesRegistry = {};
  const identifiers: string[] = [];
  
  // Create mapping from node ID to identifier for decision path conversion
  const nodeIdToIdentifier: Record<string, string> = {};
  filteredNodeIds.forEach(nodeId => {
    const node = backendNodes[nodeId];
    if (node.category) {
      nodeIdToIdentifier[nodeId] = node.category;
    }
  });
  
  filteredNodeIds.forEach((nodeId, index) => {
    const backendNode = backendNodes[nodeId];
    

    
    // Determine entity type based on node properties
    let entityType = "BOT";
    if (backendNode.role === "user") {
      entityType = "USER";
    } else if (backendNode.decision_logic) {
      entityType = "DECISION";
    }
    
    // Add identifier to list if not already present
    if (backendNode.category && !identifiers.includes(backendNode.category)) {
      identifiers.push(backendNode.category);
    }
    
    // Extract decision variables from node variables
    if (backendNode.variables) {
      Object.entries(backendNode.variables).forEach(([key, value]) => {
        if (!decisionVariables[key]) {
          // Infer type from the actual value
          let variableType: "boolean" | "string" | "number" = "boolean";
          if (typeof value === "string") {
            variableType = "string";
          } else if (typeof value === "number") {
            variableType = "number";
          }
          
          decisionVariables[key] = {
            name: key,
            type: variableType
          };
        }
      });
    }

    // Also extract variables referenced in decision logic
    if (backendNode.decision_logic?.logic) {
      const referencedVariables = extractVariablesFromJsonLogic(backendNode.decision_logic.logic);
      referencedVariables.forEach((varName: string) => {
        if (!decisionVariables[varName]) {
          decisionVariables[varName] = {
            name: varName,
            type: "boolean"
          };
        }
      });
    }
    
    // Create decision data for DECISION nodes
    let decisionData = undefined;
    if (entityType === "DECISION" && backendNode.decision_logic) {
      // Parse the JSONLogic to extract conditions
      let conditions = [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }];
      
      if (backendNode.decision_logic.logic) {
        try {
          const logic = backendNode.decision_logic.logic;
          conditions = parseJsonLogicToConditions(logic);
        } catch (e) {
          console.warn(`Failed to parse decision logic for node ${nodeId}:`, e);
          // Keep default empty condition if parsing fails
        }
      }
      
      // Convert node IDs to identifiers for truePath and falsePath
      const truePathId = backendNode.decision_logic.passed.toString();
      const falsePathId = backendNode.decision_logic.failed.toString();
      
      decisionData = {
        conditions,
        logicType: "all",
        truePath: nodeIdToIdentifier[truePathId] || truePathId,
        falsePath: nodeIdToIdentifier[falsePathId] || falsePathId,
        jsonLogic: backendNode.decision_logic.logic
      };
    }
    
    // Create tool calls data (frontend expects array format)
    let toolCalls: ToolCall[] = [];
    if (backendNode.tool_call && backendNode.tool_call.name) {
      const toolCallArguments = backendNode.tool_call.arguments || "";
      
      // Parse arguments if they're JSON string, otherwise create empty parameters
      let parameters: { name: string; value: string }[] = [];
      if (toolCallArguments) {
        try {
          // Handle both string and object cases
          let parsed;
          if (typeof toolCallArguments === "string") {
            parsed = toolCallArguments.trim() === "" ? {} : JSON.parse(toolCallArguments);
          } else if (typeof toolCallArguments === "object" && toolCallArguments !== null) {
            parsed = toolCallArguments;
          }
          
          if (typeof parsed === "object" && parsed !== null) {
            parameters = Object.entries(parsed).map(([key, value]) => ({
              name: key,
              value: String(value)
            }));
          }
        } catch (e) {
          console.warn(`Failed to parse tool call arguments for node ${nodeId}:`, {
            error: e,
            arguments: toolCallArguments
          });
        }
      }
      
      // Convert response variables from dictionary format to array format
      let responseVariables: { name: string; values: string[]; persist?: boolean }[] = [];
      if (backendNode.tool_call.response_variables && typeof backendNode.tool_call.response_variables === "object") {
        responseVariables = Object.entries(backendNode.tool_call.response_variables).map(([key, value]: [string, any]) => ({
          name: key,
          values: value?.values || [],
          persist: value?.persist ?? false
        }));
      }

      toolCalls = [{
        name: backendNode.tool_call.name,
        description: backendNode.tool_call.description || "",
        parameters,
        responseVariables
      }];
    }
    
    // Use stored positions if available, otherwise fallback to grid layout
    let x: number, y: number;
    
    if (storedPositions[nodeId]) {
      // Use stored position from backend metadata
      x = storedPositions[nodeId].x;
      y = storedPositions[nodeId].y;
    } else {
      // Fallback to grid layout for trees without stored positions
      x = (index % 4) * 300 + 100;
      y = Math.floor(index / 4) * 200 + 100;
    }
    
    // If node has tool calls, clear content to enforce mutual exclusivity
    const nodeContent = toolCalls.length > 0 
      ? "" 
      : (backendNode.content || `Node ${nodeId} content`);

    const frontendNode: Node = {
      id: nodeId,
      type: "custom",
      position: { x, y },
      data: {
        label: entityType, // Use entity type as label, not content
        content: nodeContent,
        prefix: "",
        suffix: "",
        jsonlogic: decisionData?.jsonLogic ? JSON.stringify(decisionData.jsonLogic) : "",
        isPlaceholder: false,
        entityType,
        identifier: backendNode.category,
        isDestinationNode: backendNode.is_end_node,
        isStartNode: backendNode.is_start_node,
        isDescendantOfDestination: false,
        isHighlighted: false,
        placeholders: {},
        globalRegistry: {},
        decisionData,
        toolCalls,
        variableAssignments: backendNode.variables 
          ? Object.entries(backendNode.variables).map(([variableName, value]) => ({ variableName, value }))
          : [],
        // Required callback functions (will be updated by updateNodeHandlers)
        onNodeResize: () => {},
        onPlaceholderClick: () => {},
        onAddNode: () => {},
        onEditPlaceholder: () => {},
        onEditNode: () => {},
        onDeleteNode: () => {},
        onContextMenu: () => {},
        onPreviewConversation: () => {}
      }
    };
    

    
    frontendNodes.push(frontendNode);
    
    // Create edges from next_possible_node_ids
    if (backendNode.next_possible_node_ids && backendNode.next_possible_node_ids.length > 0) {
      backendNode.next_possible_node_ids.forEach(targetNodeId => {
        edges.push({
          id: `${nodeId}-${targetNodeId}`,
          source: nodeId,
          target: targetNodeId.toString(),
          type: "custom",
          animated: false,
          data: {}
        });
      });
    }
  });
  
  // Convert backend placeholder_variables to GlobalVariableRegistry format
  const globalVariableRegistry: GlobalVariableRegistry = {};
  if (backendConfig.placeholder_variables) {
    Object.entries(backendConfig.placeholder_variables).forEach(([key, variable]) => {
      globalVariableRegistry[key] = {
        name: key,
        values: variable.values || [],
        persist: variable.persist || false
      };
    });
  }

  // Convert backend interjections to InterjectionGroup format
  const interjectionGroups: InterjectionGroup[] = [];
  if (backendConfig.interjections) {
    Object.entries(backendConfig.interjections).forEach(([groupId, interjection]: [string, any]) => {
      // Convert content items: remove interjection_type and add id
      const convertedContent = interjection.content.map((contentItem: any, index: number) => ({
        id: `${groupId}_content_${index}`, // Generate unique ID for content item
        interjection_text: contentItem.interjection_text,
        interjection_response: contentItem.interjection_response
        // Note: interjection_type is intentionally removed as frontend doesn't use it
      }));

      // Use the dictionary key as the group name and determine type from interjection_type
      const groupName = groupId;
      let frontendType: "interruption" | "FAQ" | "other";
      let customTypeName = "";
      
      // Determine frontend type based on interjection_type
      const backendType = interjection.interjection_type || "OTHER";
      switch (backendType.toUpperCase()) {
        case "INTERRUPTION":
          frontendType = "interruption";
          break;
        case "FAQ":
          frontendType = "FAQ";
          break;
        default:
          frontendType = "other";
          customTypeName = backendType;
          break;
      }

      interjectionGroups.push({
        id: groupId,
        type: frontendType,
        groupName: groupName,
        customTypeName: customTypeName,
        interjection_rate: interjection.interjection_rate || 0.1,
        within_script: interjection.within_script || false,
        repeat_script: interjection.repeat_script || false,
        content: convertedContent
      });
    });
  }

  // Create FlowData structure
  const flowData: FlowData = {
    version: "1.0.0",
    timestamp: new Date().toISOString(),
    metadata: {
      status: "draft"
    },
    data: {
      nodes: frontendNodes,
      edges,
      globalVariableRegistry,
      decisionVariablesRegistry: decisionVariables,
      identifiers,
      interjectionGroups,
      nodeIdCounter: filteredNodeIds.length > 0 ? Math.max(0, ...filteredNodeIds.map(id => Number(id)).filter(n => !Number.isNaN(n))) : 0,
      isFirstNode: frontendNodes.length === 1 && frontendNodes[0].data.isPlaceholder,
      systemPrompt: systemPrompt || undefined // Include extracted system prompt
    }
  };
  
  return flowData;
}; 